import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import {
  BaseTransaction,
  Pagination,
  Response,
  GeneralLedgerTransaction,
} from '@ui/src';
import { outgoingTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { StatusUpdate } from '../../../transactions/type';
import ROUTES from '../../../../routes';

export interface OutgoingListState {
  transactions?: BaseTransaction[];
  pagination?: Pagination;
  expandedTransactionId?: string;
  selectedCount?: number;
  collapseAllRows?: boolean;
}

const initialState: OutgoingListState = {};

export const outgoingListSlice = createSlice({
  name: 'outgoingList',
  initialState,
  reducers: {
    setExpandedTransactionId: (state, action: PayloadAction<string>) => {
      state.expandedTransactionId = action.payload;
    },
    setCollapseAllRows: (state, action: PayloadAction<boolean>) => {
      state.collapseAllRows = action.payload;
    },
    setSelectedCount: (state, action: PayloadAction<number>) => {
      state.selectedCount = action.payload;
    },

    clearOutgoingListState: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.expandedTransactionId = undefined;
      state.selectedCount = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      outgoingTransactionsListApi.endpoints.getOutgoingList.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          deletedAt: item?.node?.deletedAt || '',
          url: generatePath(ROUTES.OUTGOING_TRANSACTION, {
            transactionId: item.node.id,
          }),
          benificiaryBankName: item.node.benificiaryBankName || '',
          benificiaryBankCountry: item.node.benificiaryBankCountry || '',
          benificiaryBankCorrespondentAccount:
            item.node.benificiaryBankCorrespondentAccount || '',
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        }));

        state.transactions = nextTransactions;
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      outgoingTransactionsListApi.endpoints.getOutgoingListStatuses
        .matchFulfilled,
      (state, action: PayloadAction<Response<StatusUpdate>>) => {
        const { edges } = action.payload.data;
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          const currentTransactionStatus = edges.find(
            (transaction) => transaction.node.id === result.id,
          );

          if (
            currentTransactionStatus &&
            currentTransactionStatus.node.baseellaStatus !==
              result.baseellaStatus
          ) {
            result.baseellaStatus =
              currentTransactionStatus.node.baseellaStatus;
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      outgoingTransactionsListApi.endpoints.createOutgoingTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        let nextTransactions: BaseTransaction[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as BaseTransaction[]).concat(
            state.transactions,
          );
        }

        nextTransactions.unshift({
          ...action.payload.data,
          deletedAt: action.payload.data?.deletedAt || '',
          url: generatePath(ROUTES.OUTGOING_TRANSACTION, {
            transactionId: action.payload.data.id,
          }),
          benificiaryBankName: action.payload.data.benificiaryBankName || '',
          benificiaryBankCountry:
            action.payload.data.benificiaryBankCountry || '',
          benificiaryBankCorrespondentAccount:
            action.payload.data.benificiaryBankCorrespondentAccount || '',
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        });

        state.transactions = nextTransactions;

        if (state.selectedCount) {
          state.selectedCount += 1;
        }
      },
    );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.baseellaStatus = action.payload.data.baseellaStatus;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<GeneralLedgerTransaction>> & {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        if (state.transactions) {
          const nextGeneralLedgerTransactions = {
            edges: action.payload.data.edges.map((item) => ({
              cursor: item.cursor,
              node: {
                ...item.node,
                url: generatePath(ROUTES.OUTGOING_TRANSACTION_GL, {
                  glId: item.node.id,
                }),
              },
            })),
            pageInfo: action.payload.data.pageInfo,
          };

          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs) {
              result.generalLedgerTransactions = nextGeneralLedgerTransactions;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
  },
});

export const {
  clearOutgoingListState,
  setExpandedTransactionId,
  setSelectedCount,
  setCollapseAllRows,
} = outgoingListSlice.actions;

export default outgoingListSlice.reducer;
